<!-- Content Wrapper Start -->
<div class="fm-content-wrapper">
  <div class="container-fluid">
    <!-- Page Title -->
    <div class="fm-page-title">
      <h2 class="fm-head-24 fm-title mb-2">Settings &amp; Privacy</h2>
      <p>Manage your account, privacy, and data preferences</p>
    </div>

    <div class="fm-settings-section">
      <div class="row">
        <!-- ═══════════════════════════════════════════════════════════════
             LEFT SIDEBAR — Avatar + Tab Navigation
             ═══════════════════════════════════════════════════════════════ -->
        <div class="col-lg-4 col-xxl-auto">
          <aside class="fm-settings-sidebar fm-match-height">
            <!-- User avatar -->
            <div class="fm-settings-user">
              <div class="fm-avatar-wrap" [class.fm-has-photo]="!!profileImageUrl()">
                <!-- Profile image — shown only when a URL exists -->
                @if (profileImageUrl()) {
                  <img
                    class="fm-avatar"
                    [src]="profileImageUrl()"
                    alt="Profile"
                    [class.fm-avatar-dimmed]="isUploadingProfileImage() || isRemovingProfileImage()"
                  />
                } @else {
                  <!-- Initials fallback -->
                  <div
                    class="fm-avatar fm-avatar-initials"
                    [class.fm-avatar-dimmed]="isUploadingProfileImage() || isRemovingProfileImage()"
                    aria-label="Profile initials"
                  >
                    {{ profileInitials() }}
                  </div>
                }

                <!-- Circular progress ring overlay (during upload) -->
                @if (isUploadingProfileImage()) {
                  <div class="fm-avatar-progress-overlay">
                    <svg class="fm-progress-ring" viewBox="0 0 120 120">
                      <circle class="fm-progress-ring-bg" cx="60" cy="60" r="54" />
                      <circle
                        class="fm-progress-ring-fill"
                        cx="60"
                        cy="60"
                        r="54"
                        [style.strokeDashoffset]="339.292 - (339.292 * uploadProgress()) / 100"
                      />
                    </svg>
                    <span class="fm-progress-text">{{ uploadProgress() }}%</span>
                  </div>
                }

                <!-- Removing spinner overlay -->
                @if (isRemovingProfileImage()) {
                  <div class="fm-avatar-progress-overlay">
                    <div
                      class="spinner-border text-white"
                      role="status"
                      style="width: 1.5rem; height: 1.5rem; border-width: 2.5px"
                    >
                      <span class="visually-hidden">Removing…</span>
                    </div>
                  </div>
                }

                <!-- Camera button — only shown when no photo is set -->
                @if (!profileImageUrl() || isUploadingProfileImage() || isRemovingProfileImage()) {
                  <button
                    type="button"
                    class="fm-camera-btn"
                    aria-label="Change profile photo"
                    (click)="triggerImageUpload()"
                    [disabled]="isUploadingProfileImage() || isRemovingProfileImage()"
                  >
                    <img src="images/icons/fm-camera.svg" alt="Camera" />
                  </button>
                }

                <!-- Remove photo X button — top-right corner of avatar -->
                @if (profileImageUrl() && !isUploadingProfileImage() && !isRemovingProfileImage()) {
                  <button
                    type="button"
                    class="fm-avatar-remove-btn"
                    (click)="removeProfileImage()"
                    aria-label="Remove profile photo"
                  >
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round">
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                }

                <!-- Hidden file input -->
                <input
                  #imageInput
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  class="d-none"
                  (change)="onImageSelected($event)"
                />
              </div>

              <!-- User name + role (always "You" — this is the user's own profile) -->
              <div>
                <h5 class="fm-head-24 fm-user-name">{{ displayName() }}</h5>
                <!-- <p class="fm-user-role fm-large">You</p> -->
              </div>
            </div>

            <hr class="my-4" />

            <!-- Tab Navigation -->
            <div class="nav fm-settings-tabs" role="tablist" aria-label="Settings Tabs">
              <button
                class="nav-link"
                [class.active]="activeTab() === 'profile'"
                type="button"
                role="tab"
                [attr.aria-selected]="activeTab() === 'profile'"
                (click)="setTab('profile')"
              >
                <img src="images/icons/fm-account.svg" class="fm-svg" alt="Account & Family" />
                Profile
              </button>
              <button
                class="nav-link"
                [class.active]="activeTab() === 'security'"
                type="button"
                role="tab"
                [attr.aria-selected]="activeTab() === 'security'"
                (click)="setTab('security')"
              >
                <img src="images/icons/fm-security.svg" class="fm-svg" alt="Privacy & Security" />
                Privacy &amp; Security
              </button>
              <button
                class="nav-link"
                [class.active]="activeTab() === 'notifications'"
                type="button"
                role="tab"
                [attr.aria-selected]="activeTab() === 'notifications'"
                (click)="setTab('notifications')"
              >
                <img src="images/icons/fm-notifications.svg" class="fm-svg" alt="Notifications" />
                Notifications
              </button>
              <button
                class="nav-link disabled"
                type="button"
                role="tab"
                disabled
                aria-disabled="true"
              >
                <img src="images/icons/fm-subscription.svg" class="fm-svg" alt="Subscription" />
                Subscription
              </button>

              <!-- <button type="button" class="fm-logout-link mt-4 mt-xxl-5" (click)="logout()">
                <img src="images/icons/fm-logout-lg.svg" class="fm-svg" alt="Logout" />
                Logout
              </button> -->
            </div>
          </aside>
        </div>
        <!-- /sidebar col -->

        <!-- ═══════════════════════════════════════════════════════════════
             RIGHT CONTENT — Tab Panes
             ═══════════════════════════════════════════════════════════════ -->
        <div class="col-lg-8 mt-4 mt-lg-0 col-xxl">
          <div class="fm-settings-content">
            <!-- ── Tab 1: Account & Family ────────────────────────────── -->
            @if (activeTab() === 'profile') {
              <div class="fm-card fm-profile-info-card fm-match-height">
                <div class="fm-setting-pane-head fm-setting-pane-head-flex">
                  <div>
                    <h3 class="fm-head-24">PROFILE INFORMATION</h3>
                    <p>Update your personal information</p>
                  </div>
                  @if (!isEditingProfile() && !isLoadingProfile()) {
                    <button type="button" class="fm-edit-profile-btn" (click)="isEditingProfile.set(true)">
                      <img src="images/icons/fm-edit.svg" class="fm-svg" alt="" />
                      Edit
                    </button>
                  }
                </div>

                @if (isLoadingProfile()) {
                  <div class="text-center py-5">
                    <div
                      class="spinner-border"
                      role="status"
                      style="width: 1.75rem; height: 1.75rem; border-width: 3px"
                    >
                      <span class="visually-hidden">Loading…</span>
                    </div>
                  </div>
                } @else {
                  <form [formGroup]="profileForm" (ngSubmit)="saveProfile()" novalidate>
                    <div class="fm-setting-info-grid">
                      <!-- ── Personal Information ──────────────────────── -->
                      <div class="row g-3 g-xl-4">
                        <!-- First Name -->
                        <div class="col-sm-6">
                          <div class="form-group">
                            <label class="fm-label">
                              First Name @if (isEditingProfile()) { <span class="fm-required">*</span> }
                            </label>
                            @if (isEditingProfile()) {
                              <input
                                type="text"
                                class="form-control"
                                formControlName="firstName"
                                placeholder="Enter first name"
                                [class.is-invalid]="pf['firstName'].invalid && pf['firstName'].touched"
                                autocomplete="given-name"
                                maxlength="50"
                              />
                              @if (pf['firstName'].errors?.['required'] && pf['firstName'].touched) {
                                <div class="invalid-feedback">First name is required.</div>
                              }
                              @if (pf['firstName'].errors?.['minlength'] && pf['firstName'].touched) {
                                <div class="invalid-feedback">First name must be at least 2 characters.</div>
                              }
                              @if (pf['firstName'].errors?.['maxlength'] && pf['firstName'].touched) {
                                <div class="invalid-feedback">First name cannot exceed 50 characters.</div>
                              }
                            } @else {
                              <div class="fm-profile-field-value">{{ pf['firstName'].value || '—' }}</div>
                            }
                          </div>
                        </div>

                        <!-- Last Name -->
                        <div class="col-sm-6">
                          <div class="form-group">
                            <label class="fm-label">
                              Last Name @if (isEditingProfile()) { <span class="fm-required">*</span> }
                            </label>
                            @if (isEditingProfile()) {
                              <input
                                type="text"
                                class="form-control"
                                formControlName="lastName"
                                placeholder="Enter last name"
                                [class.is-invalid]="pf['lastName'].invalid && pf['lastName'].touched"
                                autocomplete="family-name"
                                maxlength="50"
                              />
                              @if (pf['lastName'].errors?.['required'] && pf['lastName'].touched) {
                                <div class="invalid-feedback">Last name is required.</div>
                              }
                              @if (pf['lastName'].errors?.['minlength'] && pf['lastName'].touched) {
                                <div class="invalid-feedback">Last name must be at least 2 characters.</div>
                              }
                              @if (pf['lastName'].errors?.['maxlength'] && pf['lastName'].touched) {
                                <div class="invalid-feedback">Last name cannot exceed 50 characters.</div>
                              }
                            } @else {
                              <div class="fm-profile-field-value">{{ pf['lastName'].value || '—' }}</div>
                            }
                          </div>
                        </div>

                        <!-- Phone — editable in edit mode -->
                        <div class="col-sm-6">
                          <div class="form-group">
                            <label class="fm-label">Phone Number</label>
                            @if (isEditingProfile()) {
                              <input
                                type="tel"
                                class="form-control"
                                formControlName="phoneNumber"
                                placeholder="(555) 555-5555"
                                autocomplete="tel"
                                maxlength="14"
                                appPhoneFormat
                              />
                            } @else {
                              <div class="fm-profile-field-value">{{ formatPhoneDisplay(profilePhone()) || '—' }}</div>
                            }
                          </div>
                        </div>

                        <!-- Email — always read-only -->
                        <div class="col-sm-6">
                          <div class="form-group">
                            <label class="fm-label">Email Address</label>
                            <div class="fm-profile-field-value">{{ profileEmail() || '—' }}</div>
                          </div>
                        </div>
                      </div>
                      <!-- /row personal -->
                    </div>
                    <!-- /fm-setting-info-grid -->

                    <!-- ── Save / Cancel — only visible in edit mode ─────── -->
                    @if (isEditingProfile()) {
                      <div class="fm-setting-actions mt-4">
                        <button class="fm-common-btn" type="submit" [disabled]="isSavingProfile()">
                          @if (isSavingProfile()) {
                            <span
                              class="spinner-border spinner-border-sm me-2"
                              role="status"
                              aria-hidden="true"
                            ></span>
                            Saving…
                          } @else {
                            Save Changes
                          }
                        </button>
                        <button
                          type="button"
                          class="fm-cancel-edit-btn ms-3"
                          [disabled]="isSavingProfile()"
                          (click)="cancelEditProfile()"
                        >
                          Cancel
                        </button>
                      </div>
                    }

                    <!-- ── Delete Account — hidden for social-login users ── -->
                    @if (!isSocialLogin()) {

                    <div class="fm-card fm-profile-info-card mt-4">
                      <div class="fm-setting-pane-head fm-border-none">
                        <h3 class="fm-head-24">Delete Account</h3>
                        <p>
                          Permanently delete your account and all associated data. This action
                          cannot be undone.
                        </p>
                      </div>
                      <div class="fm-setting-actions">
                        <button
                          type="button"
                          class="fm-delete-account-btn"
                          (click)="openDeleteAccountModal()"
                          [disabled]="isDeletingAccount() || isPreCheckingDeletion()"
                        >
                          @if (isPreCheckingDeletion()) {
                            <span
                              class="spinner-border spinner-border-sm me-2"
                              role="status"
                              aria-hidden="true"
                            ></span>
                            Checking…
                          } @else {
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              stroke-width="2"
                              stroke-linecap="round"
                            >
                              <polyline points="3 6 5 6 21 6"></polyline>
                              <path
                                d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"
                              ></path>
                            </svg>
                            Delete My Account
                          }
                        </button>
                      </div>
                    </div>

                    <!-- ── Delete Account Modal ──────────────────────────── -->
                    @if (showDeleteModal()) {
                      <div class="fm-modal-backdrop" (click)="closeDeleteModal()">
                        <div
                          class="fm-modal-dialog"
                          (click)="$event.stopPropagation()"
                          role="dialog"
                          aria-modal="true"
                          aria-labelledby="deleteModalTitle"
                        >
                          <!-- Header -->
                          <div class="fm-modal-header fm-modal-header-danger">
                            <div class="fm-modal-header-icon">
                              <svg
                                width="22"
                                height="22"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                              >
                                <polyline points="3 6 5 6 21 6"></polyline>
                                <path
                                  d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"
                                ></path>
                              </svg>
                            </div>
                            <div class="fm-modal-header-text">
                              <h4 id="deleteModalTitle" class="fm-modal-title">Delete Account</h4>
                              <p class="fm-modal-subtitle">
                                This action is permanent and cannot be undone
                              </p>
                            </div>
                            <button
                              type="button"
                              class="fm-modal-close"
                              (click)="closeDeleteModal()"
                              [disabled]="isDeletingAccount()"
                              aria-label="Close"
                            >
                              <svg
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2.5"
                              >
                                <line x1="18" y1="6" x2="6" y2="18" />
                                <line x1="6" y1="6" x2="18" y2="18" />
                              </svg>
                            </button>
                          </div>

                          <!-- Body -->
                          <div class="fm-modal-body">
                            <p class="fm-delete-confirm-lead">
                              Are you sure you want to delete your account? This will:
                            </p>
                            <ul class="fm-modal-consequences">
                              <li>
                                Permanently delete all personal data, stories, storybooks, legacy
                                pages, and memories.
                              </li>
                              <li>
                                Remove access to shared content for members across all family
                                groups.
                              </li>
                              <li>This action cannot be undone once confirmed.</li>
                            </ul>
                          </div>

                          <!-- Footer -->
                          <div class="fm-modal-footer">
                            <button
                              type="button"
                              class="fm-common-btn fm-secondary-btn"
                              (click)="closeDeleteModal()"
                              [disabled]="isDeletingAccount()"
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              class="fm-modal-delete-btn"
                              (click)="confirmDeleteAccount()"
                              [disabled]="isDeletingAccount()"
                            >
                              @if (isDeletingAccount()) {
                                <span
                                  class="spinner-border spinner-border-sm me-2"
                                  role="status"
                                  aria-hidden="true"
                                ></span>
                                Deleting…
                              } @else {
                                Delete Account
                              }
                            </button>
                          </div>
                        </div>
                      </div>
                    }
                    <!-- /showDeleteModal -->

                    <!-- ── Ownership Blocker Modal ────────────────────────────────────── -->
                    @if (showOwnershipBlockerModal()) {
                      <div class="fm-modal-backdrop" (click)="closeOwnershipBlockerModal()">
                        <div
                          class="fm-modal-dialog fm-modal-dialog-lg"
                          (click)="$event.stopPropagation()"
                          role="dialog"
                          aria-modal="true"
                          aria-labelledby="ownershipModalTitle"
                        >
                          <!-- Header -->
                          <div class="fm-modal-header">
                            <div class="fm-modal-header-icon fm-modal-header-icon-warn">
                              <svg
                                width="22"
                                height="22"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                              >
                                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                <circle cx="9" cy="7" r="4" />
                                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                              </svg>
                            </div>
                            <div class="fm-modal-header-text">
                              <h4 id="ownershipModalTitle" class="fm-modal-title">
                                Transfer Ownership Required
                              </h4>
                              <p class="fm-modal-subtitle">
                                You must transfer ownership before deleting your account
                              </p>
                            </div>
                            <button
                              type="button"
                              class="fm-modal-close"
                              (click)="closeOwnershipBlockerModal()"
                              aria-label="Close"
                            >
                              <svg
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2.5"
                              >
                                <line x1="18" y1="6" x2="6" y2="18" />
                                <line x1="6" y1="6" x2="18" y2="18" />
                              </svg>
                            </button>
                          </div>

                          <!-- Body -->
                          <div class="fm-modal-body">
                            <p class="fm-delete-confirm-lead">
                              You are the owner of the following family group(s). Before your
                              account can be deleted, you must transfer ownership of each group to
                              another member or remove all members.
                            </p>

                            <div class="fm-ownership-info-banner">
                              <svg
                                width="15"
                                height="15"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                              >
                                <circle cx="12" cy="12" r="10" />
                                <line x1="12" y1="8" x2="12" y2="12" />
                                <line x1="12" y1="16" x2="12.01" y2="16" />
                              </svg>
                              Go to each family group's settings, assign a new owner, then return
                              here to delete your account.
                            </div>
                          </div>

                          <!-- Footer -->
                          <div class="fm-modal-footer">
                            <button
                              type="button"
                              class="fm-common-btn"
                              (click)="closeOwnershipBlockerModal()"
                            >
                              OK, I'll Transfer Ownership
                            </button>
                          </div>
                        </div>
                      </div>
                    }
                    <!-- /showOwnershipBlockerModal -->

                    } <!-- /@if (!isSocialLogin()) -->
                  </form>
                }
                <!-- /isLoadingProfile else -->
              </div>
            }

            <!-- ── Tab 2: Privacy & Security ─────────────────────────── -->
            @if (activeTab() === 'security') {
              <!-- ── Privacy Settings card ─────────────────────────────── -->
              <div class="fm-card fm-profile-info-card">
                <div class="fm-setting-pane-head fm-border-none">
                  <h3 class="fm-head-24">Privacy Settings</h3>
                  <p>Control who can see your content and profile</p>
                </div>

                @if (isLoadingPrivacy()) {
                  <div class="text-center py-4">
                    <div
                      class="spinner-border"
                      role="status"
                      style="width: 1.75rem; height: 1.75rem; border-width: 3px"
                    >
                      <span class="visually-hidden">Loading…</span>
                    </div>
                  </div>
                } @else {
                  <!-- ── Default Story Privacy ─────────────────────────── -->
                  <div class="fm-privacy-cl">
                    <div class="d-flex align-items-center justify-content-between mb-1">
                      <p class="fm-large mb-0">Default Story Privacy</p>
                      @if (isSavingStoryPrivacy()) {
                        <div class="spinner-border spinner-border-sm text-secondary" role="status">
                          <span class="visually-hidden">Saving…</span>
                        </div>
                      }
                    </div>
                    <p class="mb-3">New stories will use this privacy setting by default</p>
                    <div class="fm-filter-tags fm-filter-tags-scroll pb-2" tabindex="0">
                      @for (opt of storyPrivacyOptions(); track opt.id) {
                        <button
                          type="button"
                          class="fm-filter-tag fm-filter-btn"
                          [class.fm-ft-active]="defaultStoryPrivacy() === opt.id"
                          [disabled]="isSavingStoryPrivacy()"
                          (click)="setDefaultStoryPrivacy(opt.id)"
                        >
                          {{ opt.name }}
                        </button>
                      }
                    </div>
                  </div>
                  <!-- /Default Story Privacy -->

                  <div class="col-12"><hr class="my-4" /></div>

                  <!-- ── Profile Visibility ────────────────────────────── -->
                  <div class="fm-privacy-cl">
                    <div class="d-flex align-items-center justify-content-between mb-1">
                      <p class="fm-large mb-0">Profile Visibility</p>
                      @if (isSavingProfileVisibility()) {
                        <div class="spinner-border spinner-border-sm text-secondary" role="status">
                          <span class="visually-hidden">Saving…</span>
                        </div>
                      }
                    </div>
                    <p class="mb-3">Who can see your profile and stories</p>
                    <div class="fm-filter-tags fm-filter-tags-scroll pb-2" tabindex="0">
                      @for (opt of profileVisibilityOptions(); track opt.id) {
                        <button
                          type="button"
                          class="fm-filter-tag fm-filter-btn"
                          [class.fm-ft-active]="profileVisibility() === opt.id"
                          [disabled]="isSavingProfileVisibility()"
                          (click)="setProfileVisibility(opt.id)"
                        >
                          {{ opt.name }}
                        </button>
                      }
                    </div>

                    <!-- Contextual info banner per selection -->
                    @if (profileVisibility() === ProfileVisibilityLevel.OnlyMe) {
                      <div class="fm-privacy-info-banner fm-privacy-info-private mt-3">
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="2"
                        >
                          <circle cx="12" cy="12" r="10" />
                          <line x1="12" y1="8" x2="12" y2="12" />
                          <line x1="12" y1="16" x2="12.01" y2="16" />
                        </svg>
                        Your profile is completely hidden. No other user can find or view it.
                      </div>
                    }
                    @if (profileVisibility() === ProfileVisibilityLevel.Family) {
                      <div class="fm-privacy-info-banner fm-privacy-info-family mt-3">
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="2"
                        >
                          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                          <circle cx="9" cy="7" r="4" />
                          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                        </svg>
                        Visible to all members across all family groups you belong to.
                      </div>
                    }
                    @if (profileVisibility() === ProfileVisibilityLevel.Public) {
                      <div class="fm-privacy-info-banner fm-privacy-info-public mt-3">
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="2"
                        >
                          <circle cx="12" cy="12" r="10" />
                          <line x1="2" y1="12" x2="22" y2="12" />
                          <path
                            d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"
                          />
                        </svg>
                        Your profile is visible to all registered users on the platform.
                      </div>
                    }
                  </div>
                  <!-- /Profile Visibility -->
                }
                <!-- /isLoadingPrivacy else -->
              </div>
              <!-- /Privacy Settings card -->

              <!-- Security card -->
              <div class="fm-card mt-4 fm-profile-info-card fm-match-height">
                <div class="fm-setting-pane-head fm-border-none">
                  <h3 class="fm-head-24">Security</h3>
                </div>

                @if (isSocialLogin()) {
                  <!-- Social login users cannot change password -->
                  <div class="fm-privacy-info-banner fm-privacy-info-family">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    Password management is not available for accounts registered via Google,
                    Facebook, or Apple sign-in.
                  </div>
                } @else {
                  <!-- Change Password toggle row -->
                  <button
                    type="button"
                    class="fm-change-password-info w-100 text-start"
                    (click)="togglePasswordForm()"
                    [attr.aria-expanded]="showPasswordForm()"
                  >
                    <div class="fm-card-content">
                      <p class="fm-card-head">Change Password</p>
                      <p>Update your account password</p>
                    </div>
                    <span>
                      <img
                        src="images/icons/fm-ar-grey.svg"
                        class="fm-svg"
                        alt="Arrow"
                        [style.transform]="showPasswordForm() ? 'rotate(90deg)' : 'none'"
                        style="transition: transform 0.2s ease"
                      />
                    </span>
                  </button>

                  @if (showPasswordForm()) {
                    <div class="fm-password-form mt-3">
                      <hr class="mt-0 mb-4" />
                      <form [formGroup]="passwordForm" (ngSubmit)="changePassword()" novalidate>
                        <div class="row g-3 g-xl-4">
                          <div class="col-12">
                            <div class="form-group">
                              <label class="fm-label"
                                >Current Password <span class="fm-required">*</span></label
                              >
                              <div class="fm-input-group">
                                <input
                                  [type]="showCurrentPassword() ? 'text' : 'password'"
                                  class="form-control"
                                  formControlName="currentPassword"
                                  placeholder="Enter current password"
                                  [class.is-invalid]="
                                    pwf['currentPassword'].invalid && pwf['currentPassword'].touched
                                  "
                                  autocomplete="current-password"
                                  maxlength="100"
                                />
                                <button
                                  type="button"
                                  class="fm-password-eye"
                                  (click)="showCurrentPassword.update(v => !v)"
                                  aria-label="Toggle current password visibility"
                                >
                                  <i
                                    [class]="
                                      showCurrentPassword() ? 'bi bi-eye-slash' : 'bi bi-eye'
                                    "
                                  ></i>
                                </button>
                              </div>
                              @if (
                                pwf['currentPassword'].errors?.['required'] &&
                                pwf['currentPassword'].touched
                              ) {
                                <div class="fm-error-msg">Current password is required.</div>
                              }
                              @if (
                                pwf['currentPassword'].errors?.['maxlength'] &&
                                pwf['currentPassword'].touched
                              ) {
                                <div class="fm-error-msg">
                                  Password cannot exceed 100 characters.
                                </div>
                              }
                            </div>
                          </div>

                          <div class="col-sm-6">
                            <div class="form-group">
                              <label class="fm-label"
                                >New Password <span class="fm-required">*</span></label
                              >
                              <div class="fm-input-group">
                                <input
                                  [type]="showNewPassword() ? 'text' : 'password'"
                                  class="form-control"
                                  formControlName="newPassword"
                                  placeholder="Enter new password"
                                  [class.is-invalid]="
                                    pwf['newPassword'].invalid && pwf['newPassword'].touched
                                  "
                                  autocomplete="new-password"
                                  maxlength="20"
                                />
                                <button
                                  type="button"
                                  class="fm-password-eye"
                                  (click)="showNewPassword.update(v => !v)"
                                  aria-label="Toggle new password visibility"
                                >
                                  <i
                                    [class]="showNewPassword() ? 'bi bi-eye-slash' : 'bi bi-eye'"
                                  ></i>
                                </button>
                              </div>
                              @if (
                                pwf['newPassword'].errors?.['required'] &&
                                pwf['newPassword'].touched
                              ) {
                                <div class="fm-error-msg">New password is required.</div>
                              }
                              <!-- Password strength hints: only when user has typed something -->
                              @if (pwf['newPassword'].dirty && pwf['newPassword'].value) {
                                <div class="fm-pw-hints">
                                  <div class="fm-pw-hint" [class.met]="passwordCriteria.minLength">
                                    <svg
                                      width="12"
                                      height="12"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="currentColor"
                                      stroke-width="3"
                                    >
                                      <polyline points="20 6 9 17 4 12"></polyline>
                                    </svg>
                                    At least 8 characters
                                  </div>
                                  <div class="fm-pw-hint" [class.met]="passwordCriteria.maxLength">
                                    <svg
                                      width="12"
                                      height="12"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="currentColor"
                                      stroke-width="3"
                                    >
                                      <polyline points="20 6 9 17 4 12"></polyline>
                                    </svg>
                                    Max 20 characters
                                  </div>
                                  <div class="fm-pw-hint" [class.met]="passwordCriteria.hasUpper">
                                    <svg
                                      width="12"
                                      height="12"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="currentColor"
                                      stroke-width="3"
                                    >
                                      <polyline points="20 6 9 17 4 12"></polyline>
                                    </svg>
                                    1 uppercase letter
                                  </div>
                                  <div class="fm-pw-hint" [class.met]="passwordCriteria.hasNumber">
                                    <svg
                                      width="12"
                                      height="12"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="currentColor"
                                      stroke-width="3"
                                    >
                                      <polyline points="20 6 9 17 4 12"></polyline>
                                    </svg>
                                    1 number
                                  </div>
                                  <div class="fm-pw-hint" [class.met]="passwordCriteria.hasSpecial">
                                    <svg
                                      width="12"
                                      height="12"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="currentColor"
                                      stroke-width="3"
                                    >
                                      <polyline points="20 6 9 17 4 12"></polyline>
                                    </svg>
                                    1 special character (@$!%*?&amp;)
                                  </div>
                                </div>
                              }
                            </div>
                          </div>

                          <div class="col-sm-6">
                            <div class="form-group">
                              <label class="fm-label"
                                >Confirm New Password <span class="fm-required">*</span></label
                              >
                              <div class="fm-input-group">
                                <input
                                  [type]="showConfirmPassword() ? 'text' : 'password'"
                                  class="form-control"
                                  formControlName="confirmPassword"
                                  placeholder="Confirm new password"
                                  [class.is-invalid]="
                                    (pwf['confirmPassword'].errors?.['required'] &&
                                      pwf['confirmPassword'].touched) ||
                                    (passwordForm.errors?.['passwordMismatch'] &&
                                      pwf['confirmPassword'].touched)
                                  "
                                  autocomplete="new-password"
                                  maxlength="20"
                                />
                                <button
                                  type="button"
                                  class="fm-password-eye"
                                  (click)="showConfirmPassword.update(v => !v)"
                                  aria-label="Toggle confirm password visibility"
                                >
                                  <i
                                    [class]="
                                      showConfirmPassword() ? 'bi bi-eye-slash' : 'bi bi-eye'
                                    "
                                  ></i>
                                </button>
                              </div>
                              @if (
                                pwf['confirmPassword'].errors?.['required'] &&
                                pwf['confirmPassword'].touched
                              ) {
                                <div class="fm-error-msg">Confirm password is required</div>
                              }
                              @if (pwf['confirmPassword'].dirty && pwf['confirmPassword'].value) {
                                <div class="fm-pw-hints">
                                  <div
                                    class="fm-pw-hint"
                                    [class.met]="
                                      !passwordForm.errors?.['passwordMismatch'] &&
                                      !!pwf['confirmPassword'].value
                                    "
                                    [class.failed]="
                                      !!passwordForm.errors?.['passwordMismatch'] &&
                                      !!pwf['confirmPassword'].value
                                    "
                                  >
                                    <!-- X icon when mismatch, tick icon when match -->
                                    @if (passwordForm.errors?.['passwordMismatch']) {
                                      <svg
                                        width="12"
                                        height="12"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        stroke-width="3"
                                      >
                                        <line x1="18" y1="6" x2="6" y2="18"></line>
                                        <line x1="6" y1="6" x2="18" y2="18"></line>
                                      </svg>
                                      Passwords do not match
                                    } @else {
                                      <svg
                                        width="12"
                                        height="12"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        stroke-width="3"
                                      >
                                        <polyline points="20 6 9 17 4 12"></polyline>
                                      </svg>
                                      Passwords match
                                    }
                                  </div>
                                </div>
                              }
                            </div>
                          </div>

                          <div class="col-12">
                            <div class="fm-setting-actions">
                              <button
                                class="fm-common-btn mt-2"
                                type="submit"
                                [disabled]="isChangingPassword()"
                              >
                                @if (isChangingPassword()) {
                                  <span
                                    class="spinner-border spinner-border-sm me-2"
                                    role="status"
                                    aria-hidden="true"
                                  ></span>
                                  Updating…
                                } @else {
                                  Update Password
                                }
                              </button>
                              <button
                                type="button"
                                class="fm-common-btn fm-secondary-btn ms-3 mt-2"
                                (click)="cancelPasswordForm()"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        </div>
                      </form>
                    </div>
                  }
                  <!-- /showPasswordForm -->
                }
                <!-- /isSocialLogin else -->
              </div>
              <!-- /Security card -->
            }
            <!-- /security tab -->

            <!-- ── Tab 3: Notifications ───────────────────────────────── -->
            @if (activeTab() === 'notifications') {
              <div class="fm-card fm-profile-info-card fm-match-height">
                <div class="fm-setting-pane-head fm-border-none">
                  <h3 class="fm-head-24">NOTIFICATIONS</h3>
                  <p>Choose which events you want to be notified about</p>
                </div>

                @if (isLoadingNotifications()) {
                  <div class="text-center py-4">
                    <div
                      class="spinner-border"
                      role="status"
                      style="width: 1.75rem; height: 1.75rem; border-width: 3px"
                    >
                      <span class="visually-hidden">Loading…</span>
                    </div>
                  </div>
                } @else {
                  <div class="fm-setting-block">
                    <div class="fm-switch-row">
                      <div class="fm-switch-text">
                        <p class="fm-large mb-1">New Member Additions</p>
                        <small class="fm-field-hint"
                          >Notify me when a new member joins any of my family groups</small
                        >
                      </div>
                      <div class="form-check form-switch">
                        <input
                          class="form-check-input"
                          type="checkbox"
                          role="switch"
                          id="fmNotifMembers"
                          [checked]="notifyNewMemberAdditions()"
                          (change)="notifyNewMemberAdditions.set($any($event.target).checked)"
                        />
                      </div>
                    </div>

                    <div class="fm-switch-row">
                      <div class="fm-switch-text">
                        <p class="fm-large mb-1">Scheduled Prompts</p>
                        <small class="fm-field-hint"
                          >Notify me when a scheduled prompt is delivered or a member
                          responds</small
                        >
                      </div>
                      <div class="form-check form-switch">
                        <input
                          class="form-check-input"
                          type="checkbox"
                          role="switch"
                          id="fmNotifPrompts"
                          [checked]="notifyScheduledPrompts()"
                          (change)="notifyScheduledPrompts.set($any($event.target).checked)"
                        />
                      </div>
                    </div>
                  </div>
                  <div class="fm-setting-actions">
                    <button
                      class="fm-common-btn mt-4"
                      type="button"
                      [disabled]="isSavingNotifications()"
                      (click)="saveNotifications()"
                    >
                      @if (isSavingNotifications()) {
                        <span
                          class="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Saving…
                      } @else {
                        Save Changes
                      }
                    </button>
                  </div>
                }
                <!-- /isLoadingNotifications else -->
              </div>
            }
            <!-- /notifications tab -->

            <!-- ── Tab 4: Subscription ───────────────────────────────── -->
            @if (activeTab() === 'subscription') {
              <!-- Choose Plan card -->
              <div class="fm-card fm-plan-wrapper">
                <div class="fm-setting-pane-head fm-border-none">
                  <h3 class="fm-head-24">Choose Your Plan</h3>
                  <p>Select the plan that best fits your family's storytelling needs</p>
                </div>
                <div class="fm-subscription-table">
                  <div class="row g-4 g-xl-5">
                    <!-- Free -->
                    <div class="col-12 col-lg-4 col-xl-6 col-xxl-4">
                      <div class="fm-plan-card">
                        <div class="fm-plan-card-body">
                          <h4 class="fm-head-24 fm-plan-title">Free</h4>
                          <div class="fm-plan-price">
                            <h2 class="fm-head-50">$0.00</h2>
                            <span>/ month</span>
                          </div>
                          <p class="fm-plan-text">Perfect for getting started</p>
                          <ul class="fm-plan-list fm-check-list">
                            <li>
                              <span class="fm-check-icon"
                                ><img
                                  src="images/icons/fm-check-gry.svg"
                                  class="fm-svg"
                                  alt="Included" /></span
                              ><span>5 family members</span>
                            </li>
                            <li>
                              <span class="fm-check-icon"
                                ><img
                                  src="images/icons/fm-check-gry.svg"
                                  class="fm-svg"
                                  alt="Included" /></span
                              ><span>50 stories per month</span>
                            </li>
                            <li>
                              <span class="fm-check-icon"
                                ><img
                                  src="images/icons/fm-check-gry.svg"
                                  class="fm-svg"
                                  alt="Included" /></span
                              ><span>Basic video &amp; audio recording</span>
                            </li>
                            <li>
                              <span class="fm-check-icon"
                                ><img
                                  src="images/icons/fm-check-gry.svg"
                                  class="fm-svg"
                                  alt="Included" /></span
                              ><span>AI-powered prompts</span>
                            </li>
                            <li>
                              <span class="fm-check-icon"
                                ><img
                                  src="images/icons/fm-check-gry.svg"
                                  class="fm-svg"
                                  alt="Included" /></span
                              ><span>JSON export only</span>
                            </li>
                            <li>
                              <span class="fm-check-icon"
                                ><img
                                  src="images/icons/fm-check-gry.svg"
                                  class="fm-svg"
                                  alt="Included" /></span
                              ><span>5GB cloud storage</span>
                            </li>
                          </ul>
                        </div>
                        <div class="fm-plan-footer">
                          <button class="fm-plan-btn fm-plan-btn-disabled" type="button" disabled>
                            Current Plan
                            <img src="images/icons/fm-ar-right-lg.svg" class="fm-svg" alt="" />
                          </button>
                        </div>
                      </div>
                    </div>

                    <!-- Family (Most Popular) -->
                    <div class="col-12 col-lg-4 col-xl-6 col-xxl-4">
                      <div class="fm-plan-card active">
                        <div class="fm-head-22 fm-plan-badge">Most Popular</div>
                        <div class="fm-plan-card-body">
                          <h4 class="fm-head-24 fm-plan-title">Family</h4>
                          <div class="fm-plan-price fm-plan-price-primary">
                            <h2 class="fm-head-50">$19</h2>
                            <span>/ month</span>
                          </div>
                          <p class="fm-plan-text">Complete family legacy</p>
                          <ul class="fm-plan-list fm-plan-list-primary fm-check-list">
                            <li>
                              <span class="fm-check"
                                ><img
                                  src="images/icons/fm-ar-primary-lg.svg"
                                  class="fm-svg"
                                  alt="Included" /></span
                              ><span>15 family members</span>
                            </li>
                            <li>
                              <span class="fm-check"
                                ><img
                                  src="images/icons/fm-ar-primary-lg.svg"
                                  class="fm-svg"
                                  alt="Included" /></span
                              ><span>Unlimited stories</span>
                            </li>
                            <li>
                              <span class="fm-check"
                                ><img
                                  src="images/icons/fm-ar-primary-lg.svg"
                                  class="fm-svg"
                                  alt="Included" /></span
                              ><span>HD video &amp; audio recording</span>
                            </li>
                            <li>
                              <span class="fm-check"
                                ><img
                                  src="images/icons/fm-ar-primary-lg.svg"
                                  class="fm-svg"
                                  alt="Included" /></span
                              ><span>Advanced AI organization</span>
                            </li>
                            <li>
                              <span class="fm-check"
                                ><img
                                  src="images/icons/fm-ar-primary-lg.svg"
                                  class="fm-svg"
                                  alt="Included" /></span
                              ><span>PDF &amp; digital storybook export</span>
                            </li>
                            <li>
                              <span class="fm-check"
                                ><img
                                  src="images/icons/fm-ar-primary-lg.svg"
                                  class="fm-svg"
                                  alt="Included" /></span
                              ><span>Legacy QR codes</span>
                            </li>
                            <li>
                              <span class="fm-check"
                                ><img
                                  src="images/icons/fm-ar-primary-lg.svg"
                                  class="fm-svg"
                                  alt="Included" /></span
                              ><span>50GB cloud storage</span>
                            </li>
                          </ul>
                        </div>
                        <div class="fm-plan-footer">
                          <button class="fm-plan-btn fm-plan-btn-primary" type="button">
                            Upgrade to Family
                          </button>
                        </div>
                      </div>
                    </div>

                    <!-- Legacy -->
                    <div class="col-12 col-lg-4 col-xl-6 col-xxl-4">
                      <div class="fm-plan-card fm-plan-card-legacy">
                        <div class="fm-plan-card-body">
                          <h4 class="fm-head-24 fm-plan-title">Legacy</h4>
                          <div class="fm-plan-price fm-plan-price-secondary">
                            <h2 class="fm-head-50">$49</h2>
                            <span>/ month</span>
                          </div>
                          <p class="fm-plan-text">Complete family legacy</p>
                          <ul class="fm-plan-list fm-plan-list-secondary fm-check-list">
                            <li>
                              <span class="fm-check"
                                ><img
                                  src="images/icons/fm-ar-secondary-lg.svg"
                                  class="fm-svg"
                                  alt="Included" /></span
                              ><span>Unlimited family members</span>
                            </li>
                            <li>
                              <span class="fm-check"
                                ><img
                                  src="images/icons/fm-ar-secondary-lg.svg"
                                  class="fm-svg"
                                  alt="Included" /></span
                              ><span>Unlimited stories</span>
                            </li>
                            <li>
                              <span class="fm-check"
                                ><img
                                  src="images/icons/fm-ar-secondary-lg.svg"
                                  class="fm-svg"
                                  alt="Included" /></span
                              ><span>4K video recording</span>
                            </li>
                            <li>
                              <span class="fm-check"
                                ><img
                                  src="images/icons/fm-ar-secondary-lg.svg"
                                  class="fm-svg"
                                  alt="Included" /></span
                              ><span>Priority AI transcription</span>
                            </li>
                            <li>
                              <span class="fm-check"
                                ><img
                                  src="images/icons/fm-ar-secondary-lg.svg"
                                  class="fm-svg"
                                  alt="Included" /></span
                              ><span>Printed hardcover storybooks</span>
                            </li>
                            <li>
                              <span class="fm-check"
                                ><img
                                  src="images/icons/fm-ar-secondary-lg.svg"
                                  class="fm-svg"
                                  alt="Included" /></span
                              ><span>All digital export formats</span>
                            </li>
                            <li>
                              <span class="fm-check"
                                ><img
                                  src="images/icons/fm-ar-secondary-lg.svg"
                                  class="fm-svg"
                                  alt="Included" /></span
                              ><span>Lifetime archive guarantee</span>
                            </li>
                            <li>
                              <span class="fm-check"
                                ><img
                                  src="images/icons/fm-ar-secondary-lg.svg"
                                  class="fm-svg"
                                  alt="Included" /></span
                              ><span>Unlimited cloud storage</span>
                            </li>
                            <li>
                              <span class="fm-check"
                                ><img
                                  src="images/icons/fm-ar-secondary-lg.svg"
                                  class="fm-svg"
                                  alt="Included" /></span
                              ><span>Premium support</span>
                            </li>
                          </ul>
                        </div>
                        <div class="fm-plan-footer">
                          <button class="fm-plan-btn fm-plan-btn-secondary" type="button">
                            Upgrade to Legacy
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <!-- /Choose Plan card -->

              <!-- Plan Comparison card -->
              <div class="fm-card fm-plan-comparison-wrapper mt-4">
                <div class="fm-setting-pane-head fm-border-none">
                  <h3 class="fm-head-24">Plan Comparison</h3>
                  <p>Select the plan that best fits your family's storytelling needs</p>
                </div>
                <div class="table-responsive">
                  <table class="table fm-plan-table">
                    <thead>
                      <tr>
                        <th class="fm-features-column">Features</th>
                        <th>Free</th>
                        <th class="active fm-plan-col-family">Family</th>
                        <th>Legacy</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td class="fm-features-column">Family Members</td>
                        <td>5</td>
                        <td class="fm-plan-col-family">15</td>
                        <td>Unlimited</td>
                      </tr>
                      <tr>
                        <td class="fm-features-column">Monthly Stories</td>
                        <td>50</td>
                        <td class="fm-plan-col-family">Unlimited</td>
                        <td>Unlimited</td>
                      </tr>
                      <tr>
                        <td class="fm-features-column">Video Quality</td>
                        <td>720p</td>
                        <td class="fm-plan-col-family">1080p HD</td>
                        <td>4K</td>
                      </tr>
                      <tr>
                        <td class="fm-features-column">Cloud Storage</td>
                        <td>5 GB</td>
                        <td class="fm-plan-col-family">50 GB</td>
                        <td>Unlimited</td>
                      </tr>
                      <tr>
                        <td class="fm-features-column">JSON Export</td>
                        <td>
                          <img
                            src="images/icons/fm-check-green.svg"
                            class="fm-plan-icon"
                            alt="Included"
                          />
                        </td>
                        <td class="fm-plan-col-family">
                          <img
                            src="images/icons/fm-check-green.svg"
                            class="fm-plan-icon"
                            alt="Included"
                          />
                        </td>
                        <td>
                          <img
                            src="images/icons/fm-check-green.svg"
                            class="fm-plan-icon"
                            alt="Included"
                          />
                        </td>
                      </tr>
                      <tr>
                        <td class="fm-features-column">PDF Storybooks</td>
                        <td>
                          <img
                            src="images/icons/fm-x-red.svg"
                            class="fm-plan-icon"
                            alt="Not included"
                          />
                        </td>
                        <td class="fm-plan-col-family">
                          <img
                            src="images/icons/fm-check-green.svg"
                            class="fm-plan-icon"
                            alt="Included"
                          />
                        </td>
                        <td>
                          <img
                            src="images/icons/fm-check-green.svg"
                            class="fm-plan-icon"
                            alt="Included"
                          />
                        </td>
                      </tr>
                      <tr>
                        <td class="fm-features-column">Digital Storybooks</td>
                        <td>
                          <img
                            src="images/icons/fm-x-red.svg"
                            class="fm-plan-icon"
                            alt="Not included"
                          />
                        </td>
                        <td class="fm-plan-col-family">
                          <img
                            src="images/icons/fm-check-green.svg"
                            class="fm-plan-icon"
                            alt="Included"
                          />
                        </td>
                        <td>
                          <img
                            src="images/icons/fm-check-green.svg"
                            class="fm-plan-icon"
                            alt="Included"
                          />
                        </td>
                      </tr>
                      <tr>
                        <td class="fm-features-column">Printed Hardcover Books</td>
                        <td>
                          <img
                            src="images/icons/fm-x-red.svg"
                            class="fm-plan-icon"
                            alt="Not included"
                          />
                        </td>
                        <td class="fm-plan-col-family">
                          <img
                            src="images/icons/fm-x-red.svg"
                            class="fm-plan-icon"
                            alt="Not included"
                          />
                        </td>
                        <td>
                          <img
                            src="images/icons/fm-check-green.svg"
                            class="fm-plan-icon"
                            alt="Included"
                          />
                        </td>
                      </tr>
                      <tr>
                        <td class="fm-features-column">Legacy QR Codes</td>
                        <td>
                          <img
                            src="images/icons/fm-x-red.svg"
                            class="fm-plan-icon"
                            alt="Not included"
                          />
                        </td>
                        <td class="fm-plan-col-family">
                          <img
                            src="images/icons/fm-check-green.svg"
                            class="fm-plan-icon"
                            alt="Included"
                          />
                        </td>
                        <td>
                          <img
                            src="images/icons/fm-check-green.svg"
                            class="fm-plan-icon"
                            alt="Included"
                          />
                        </td>
                      </tr>
                      <tr>
                        <td class="fm-features-column">Priority Support</td>
                        <td>
                          <img
                            src="images/icons/fm-x-red.svg"
                            class="fm-plan-icon"
                            alt="Not included"
                          />
                        </td>
                        <td class="fm-plan-col-family">
                          <img
                            src="images/icons/fm-x-red.svg"
                            class="fm-plan-icon"
                            alt="Not included"
                          />
                        </td>
                        <td>
                          <img
                            src="images/icons/fm-check-green.svg"
                            class="fm-plan-icon"
                            alt="Included"
                          />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <!-- /Plan Comparison card -->
            }
            <!-- /subscription tab -->
          </div>
          <!-- /fm-settings-content -->
        </div>
        <!-- /right col -->
      </div>
      <!-- /row -->
    </div>
    <!-- /fm-settings-section -->
  </div>
  <!-- /container-fluid -->
</div>
<!-- /fm-content-wrapper -->
<!-- Content Wrapper End -->
